// WorkspaceSettings.tsx — "/workspace" (workspace-first shell spec, 2026-08-21). Deliberately
// minimal: rename + an honest note about the initial avatar. No delete / transfer / leave —
// out of scope by the spec.
import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SettingsIcon } from 'lucide-react';
import type { WorkspaceSelfDto } from '@repo/shared-types';
import { Button } from '@repo/ui/components/button';
import { EmptyState } from '@repo/ui/components/empty-state';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import { Skeleton } from '@repo/ui/components/skeleton';
import { LogoCropDialog } from '../components/LogoCropDialog';
import { PageHeader } from '../components/PageHeader';
import { WorkspaceAvatar } from '../components/shell/WorkspaceAvatar';
import { useMyWorkspaces } from '../hooks/useMyWorkspaces';
import { apiFetch, ApiError } from '../lib/api';
import { useT } from '../locales/context';

const NAME_MIN = 2;
const NAME_MAX = 60;

export function WorkspaceSettings() {
  const t = useT();
  const qc = useQueryClient();
  const { data: workspaces, isPending, error } = useMyWorkspaces();
  const active = workspaces?.find((w) => w.isActive);
  const canManage = active?.role === 'owner' || active?.role === 'admin';

  // Syncs `name` off the fetched workspace via React's "adjusting state during rendering"
  // pattern (guarded setState in the render body, not a `useEffect` — same idiom Billing.tsx's
  // `baselinePlan` uses, see that file's own comment) rather than a `useEffect`, which
  // `eslint-plugin-react-hooks`'s `set-state-in-effect` rule flags as a cascading-render risk.
  // `syncedKey` is `id:name` so a rename that lands from elsewhere (another tab, a refetch)
  // re-syncs too, not just a workspace switch.
  const [name, setName] = useState('');
  const [syncedKey, setSyncedKey] = useState<string | undefined>(undefined);
  const activeKey = active ? `${active.id}:${active.name}` : undefined;
  if (active && activeKey !== syncedKey) {
    setSyncedKey(activeKey);
    setName(active.name);
  }

  const rename = useMutation({
    mutationFn: (next: string) =>
      apiFetch<WorkspaceSelfDto>('/api/workspace', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: next }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', 'workspaces'] });
    },
  });

  // File picker is a plain hidden input, not a styled dropzone — the "Upload logo" button just
  // proxies a click to it, then the picked file is handed to LogoCropDialog for the actual
  // crop-before-upload step (spec §A: "the canvas output is what is stored, the server never
  // resizes").
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);

  const uploadLogo = useMutation({
    mutationFn: (blob: Blob) => {
      const form = new FormData();
      form.append('file', blob, 'logo.webp');
      // No Content-Type header: `apiFetch` doesn't force one, so passing a FormData body lets
      // the browser set the multipart boundary itself (see lib/api.ts's `apiFetch`).
      return apiFetch<WorkspaceSelfDto>('/api/workspace/logo', { method: 'POST', body: form });
    },
    onSuccess: () => {
      setLogoError(null);
      void qc.invalidateQueries({ queryKey: ['me', 'workspaces'] });
    },
    onError: (err) => {
      setLogoError(
        err instanceof ApiError && err.code === 'MEDIA_NOT_CONFIGURED'
          ? t.workspace.logoNotConfiguredError
          : t.workspace.uploadLogoErrorFallback,
      );
    },
  });

  const removeLogo = useMutation({
    mutationFn: () => apiFetch<WorkspaceSelfDto>('/api/workspace/logo', { method: 'DELETE' }),
    onSuccess: () => {
      setLogoError(null);
      void qc.invalidateQueries({ queryKey: ['me', 'workspaces'] });
    },
    onError: () => setLogoError(t.workspace.removeLogoErrorFallback),
  });

  const trimmed = name.trim();
  const valid = trimmed.length >= NAME_MIN && trimmed.length <= NAME_MAX;
  const dirty = !!active && trimmed !== active.name;

  if (isPending) {
    return (
      <div className="flex max-w-2xl flex-col gap-6">
        <PageHeader title={t.workspace.pageTitle} />
        <Skeleton className="h-40 rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex max-w-2xl flex-col gap-6">
        <PageHeader title={t.workspace.pageTitle} />
        <p className="text-[13px] text-destructive">{t.workspace.loadErrorMessage}</p>
      </div>
    );
  }

  if (!active) return null;

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <PageHeader title={t.workspace.pageTitle} />
      {!canManage ? (
        <EmptyState
          icon={SettingsIcon}
          title={t.workspace.notAllowedTitle}
          description={t.workspace.notAllowedDescription}
        />
      ) : (
        <div className="rounded-xl border bg-card p-5">
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (valid && dirty) rename.mutate(trimmed);
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="workspace-name">{t.workspace.nameLabel}</Label>
              <Input
                id="workspace-name"
                value={name}
                maxLength={NAME_MAX}
                onChange={(e) => setName(e.target.value)}
              />
              <p className="text-[12.5px] text-muted-foreground">
                {t.workspace.nameHint({ min: NAME_MIN, max: NAME_MAX })}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <WorkspaceAvatar name={trimmed || active.name} id={active.id} size={64} logoUrl={active.logoUrl} />
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {t.workspace.uploadLogoCta}
                  </Button>
                  {active.logoUrl && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={removeLogo.isPending}
                      onClick={() => removeLogo.mutate()}
                    >
                      {t.workspace.removeLogoCta}
                    </Button>
                  )}
                </div>
                <p className="text-[12.5px] text-muted-foreground">{t.workspace.logoHint}</p>
                {logoError && <p className="text-[13px] text-destructive">{logoError}</p>}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={(e) => {
                  const picked = e.target.files?.[0] ?? null;
                  setPendingFile(picked);
                  e.target.value = '';
                }}
              />
            </div>
            {rename.isError && (
              <p className="text-[13px] text-destructive">{t.workspace.saveNameErrorFallback}</p>
            )}
            <div className="flex items-center gap-3">
              <Button type="submit" disabled={!valid || !dirty || rename.isPending}>
                {t.workspace.saveCta}
              </Button>
              {rename.isSuccess && !dirty && (
                <span className="text-[12.5px] text-muted-foreground">{t.workspace.savedLabel}</span>
              )}
            </div>
          </form>
        </div>
      )}
      <LogoCropDialog
        file={pendingFile}
        onClose={() => setPendingFile(null)}
        onCropped={(blob) => uploadLogo.mutate(blob)}
      />
    </div>
  );
}
